// Piezas visuales comunes de la Sala de Control: tarjeta de un número, caja con título, barras
// horizontales, barras por hora y líneas por hora. SVG propio y tokens de la oficina (sin colores
// sueltos): pocas gráficas sencillas no justifican una librería.
import { Info } from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { cn } from '../../../lib/utils';
import { PistaAyuda } from '../ui';
import { t } from '../../../i18n';
import { dineroExacto, escalaRedonda, horasEje, horaUtc, miles, numero, SIN_VALOR } from './formato';
import { Etiqueta, Exacto, FOCO, franjaHora, usePuntero, type FilaEtiqueta } from './Puntero';
import { useTamano } from './useTamano';

export function Tarjeta({
  titulo,
  valor,
  detalle,
  ayuda,
  tono = 'neutro',
  texto = false,
  exacto,
}: {
  titulo: string;
  valor: string;
  /** La cifra sin redondear, al pasar el ratón por `valor`. */
  exacto?: string;
  detalle?: ReactNode;
  ayuda?: string;
  tono?: 'bueno' | 'malo' | 'aviso' | 'neutro';
  /** El valor es texto (nombres), no una cifra: letra más pequeña en una sola línea (Pau 10-10: «Modelo normal / Brazo
   *  «abril»» partía la tarjeta en dos líneas grandes). Si aun así no cabe, se corta con «…» y va entero en el title. */
  texto?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-o-m border-t-[3px] bg-oficina-elevada px-3.5 pb-3 pt-3',
        { bueno: 'border-oficina-trabajando', malo: 'border-oficina-error', aviso: 'border-oficina-bloqueado', neutro: 'border-oficina-esperando' }[tono],
      )}
    >
      <div className="flex items-center gap-1.5 whitespace-nowrap text-o-sub text-oficina-texto2">
        {titulo}
        {ayuda ? (
          <PistaAyuda texto={ayuda}>
            {/* Sin el mínimo de 44 px del botón (la tarjeta con «i» quedaba más alta que las demás de su fila): la zona
                táctil la da el ::after, como en Gasto. */}
            <button type="button" aria-label={ayuda} className="relative inline-flex h-4 min-h-0 w-4 min-w-0 items-center justify-center text-oficina-texto3 after:absolute after:-inset-3 hover:text-oficina-texto">
              <Info className="h-3.5 w-3.5" aria-hidden />
            </button>
          </PistaAyuda>
        ) : null}
      </div>
      {texto ? (
        <p title={valor} className="my-2 truncate text-o-titulo3 font-semibold leading-[30px]">
          {valor}
        </p>
      ) : (
        exacto ? (
          <Exacto filas={[{ etiqueta: '', valor: exacto }]}>
            <p className="my-1.5 text-o-titulo font-semibold tracking-tight">{valor}</p>
          </Exacto>
        ) : (
          <p className="my-1.5 text-o-titulo font-semibold tracking-tight">{valor}</p>
        )
      )}
      {detalle ? <p className="text-o-sub text-oficina-texto3">{detalle}</p> : null}
    </div>
  );
}

/** Mientras un rincón se calcula por primera vez: la forma de la pestaña (cuatro cifras y una gráfica) latiendo y
 * una línea que dice qué pasa. Nunca «no hay datos» (22-10, revisión de la sala: parecía rota). */
export function Calculando() {
  return (
    <div className="flex flex-col gap-3" role="status" aria-live="polite">
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[92px] animate-pulse rounded-o-m bg-oficina-elevada" />
        ))}
      </div>
      <div className="flex h-[220px] animate-pulse items-center justify-center rounded-o-m bg-oficina-elevada">
        <p className="text-o-texto text-oficina-texto2">{t('salaControl.rincones.calculandoPrimera')}</p>
      </div>
    </div>
  );
}

/** Rincón ya calculado sin nada que enseñar: una caja con el motivo, no una línea suelta en una pantalla vacía. */
export function SinDatos({ texto }: { texto: string }) {
  return (
    <div className="rounded-o-m bg-oficina-elevada px-5 py-10 text-center">
      <p className="text-o-texto text-oficina-texto2">{texto}</p>
    </div>
  );
}

export function Caja({ titulo, sub, derecha, children, className }: { titulo: string; sub?: string; derecha?: ReactNode; children: ReactNode; className?: string }) {
  return (
    // Columna flexible: una gráfica con `flex-1` ocupa el alto que deja la cabecera (con alto fijo, las fechas del eje se
    // salían por abajo).
    <section className={cn('flex flex-col rounded-o-m bg-oficina-elevada px-4 pb-3 pt-3.5', className)}>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-o-cuerpo font-semibold">{titulo}</h3>
          {sub ? <p className="text-o-sub text-oficina-texto2">{sub}</p> : null}
        </div>
        {derecha}
      </div>
      {children}
    </section>
  );
}

export function Leyenda({ items }: { items: Array<{ texto: string; clase: string }> }) {
  return (
    <div className="flex gap-4 text-o-nota text-oficina-texto2">
      {items.map((i) => (
        <span key={i.texto} className="flex items-center gap-1.5">
          <i className={cn('inline-block h-[3px] w-3.5 rounded', i.clase)} />
          {i.texto}
        </span>
      ))}
    </div>
  );
}

/** Barras horizontales: una fila por elemento, con su valor a la derecha. */
export function BarrasH({ filas, formato, max }: { filas: Array<{ etiqueta: string; valor: number; clase?: string }>; formato: (v: number) => string; max?: number }) {
  const tope = max ?? Math.max(1, ...filas.map((f) => f.valor));
  return (
    <div className="flex flex-col gap-2">
      {filas.map((f) => (
        <div key={f.etiqueta} className="grid grid-cols-[minmax(9rem,14rem)_1fr_5rem] items-center gap-3 text-o-sub">
          <span className="truncate text-oficina-texto2">{f.etiqueta}</span>
          <span className="h-[18px] overflow-hidden rounded bg-oficina-hover">
            <span className={cn('block h-full rounded', f.clase ?? 'bg-oficina-te-necesita')} style={{ width: `${Math.max(1.5, (100 * f.valor) / tope)}%` }} />
          </span>
          <span className="text-right font-semibold">{formato(f.valor)}</span>
        </div>
      ))}
    </div>
  );
}

const ANCHO = 960;
const IZQ = 56;
const DER = 20;
const ARRIBA = 10;
const ABAJO = 26;

/** Lienzo de una gráfica por hora: se dibuja al ancho real medido (letras nítidas) y sabe qué hora tiene el ratón. */
function useLienzoHora(hayDato: (h: number) => boolean) {
  const [ref, tam] = useTamano<HTMLDivElement>();
  const ancho = tam?.ancho ?? ANCHO;
  const x = (hora: number) => IZQ + ((ancho - IZQ - DER) * hora) / 24;
  const p = usePuntero({
    n: 24,
    ancho,
    indiceDe: (px) => {
      const h = Math.floor(((px - IZQ) / (ancho - IZQ - DER)) * 24);
      return h >= 0 && h < 24 && hayDato(h) ? h : null;
    },
    valido: hayDato,
  });
  return { ref, ancho, x, p };
}

/** Ejes de las gráficas por hora: marcas redondas en vertical (escalaRedonda) y las horas UTC cada 3 h (cada 6 h si no caben). */
function Ejes({ ancho, alto, marcas, formatoY, referencia }: { ancho: number; alto: number; marcas: number[]; formatoY: (v: number) => string; referencia: Date }) {
  const max = marcas[marcas.length - 1]!;
  const h = alto - ABAJO - ARRIBA;
  const y = (v: number) => ARRIBA + h * (1 - v / max);
  const x = (hora: number) => IZQ + ((ancho - IZQ - DER) * hora) / 24;
  return (
    <>
      {marcas.map((v) => (
        <g key={v}>
          <line x1={IZQ} x2={ancho - DER} y1={y(v)} y2={y(v)} className="stroke-oficina-linea" />
          <text x={IZQ - 8} y={y(v) + 4} textAnchor="end" className="fill-oficina-texto3 text-o-nota">
            {formatoY(v)}
          </text>
        </g>
      ))}
      {horasEje(ancho - IZQ - DER).map((hora) => (
        <text key={hora} x={x(hora)} y={alto - 6} textAnchor="middle" className="fill-oficina-texto3 text-o-nota">
          {horaUtc(hora, referencia)}
        </text>
      ))}
    </>
  );
}

/** Barras por hora UTC (hoy) con una línea de referencia (ayer). */
export function BarrasHora({ barras, linea, referencia, etiqueta, alto = 200 }: { barras: Array<number | null>; linea: number[]; referencia: Date; etiqueta: string; alto?: number }) {
  const { tope: max, marcas } = escalaRedonda(Math.max(0, ...linea, ...barras.filter((v): v is number => v !== null)));
  const { ref, ancho, x, p } = useLienzoHora((h) => barras[h] != null || linea[h] != null);
  const h = alto - ABAJO - ARRIBA;
  const paso = (ancho - IZQ - DER) / 24;
  const y = (v: number) => ARRIBA + h * (1 - v / max);
  const hm = p.i;
  return (
    <div ref={ref} className="w-full">
      <div {...p.props} className={cn('relative', FOCO)} aria-label={etiqueta}>
        <svg viewBox={`0 0 ${ancho} ${alto}`} className="block h-auto w-full" role="img" aria-label={etiqueta}>
          {hm !== null ? <rect x={x(hm) + 1} y={ARRIBA - 4} width={paso - 2} height={h + 4} rx={4} className="fill-oficina-hover" /> : null}
          <Ejes ancho={ancho} alto={alto} marcas={marcas} formatoY={(v) => `${miles(v)} k$`} referencia={referencia} />
          {barras.map((v, i) =>
            v === null ? null : (
              <rect
                key={i}
                x={x(i) + Math.min(4, paso * 0.15)}
                y={y(v)}
                width={Math.max(1, paso - 2 * Math.min(4, paso * 0.15))}
                height={Math.max(0, alto - ABAJO - y(v))}
                rx={3}
                className={cn('fill-oficina-marca-oscura', hm !== null && hm !== i && 'opacity-60')}
              />
            ),
          )}
          <polyline points={linea.map((v, i) => `${x(i + 0.5).toFixed(1)},${y(v).toFixed(1)}`).join(' ')} fill="none" strokeWidth={2} className="stroke-oficina-texto3" />
          {hm !== null && linea[hm] != null ? <circle cx={x(hm + 0.5)} cy={y(linea[hm]!)} r={4} strokeWidth={2} className="fill-oficina-texto3 stroke-oficina-elevada" /> : null}
        </svg>
        {hm !== null && p.activa ? (
          <Etiqueta
            x={x(hm + 0.5)}
            ancho={ancho}
            arriba={ARRIBA}
            titulo={franjaHora(hm)}
            filas={[
              { etiqueta: t('salaControl.hoy'), valor: barras[hm] == null ? SIN_VALOR : dineroExacto(barras[hm]!), marca: 'bg-oficina-marca-oscura', forma: 'barra' },
              { etiqueta: t('salaControl.ayerLeyenda'), valor: linea[hm] == null ? SIN_VALOR : dineroExacto(linea[hm]!), marca: 'bg-oficina-texto3', tenue: true },
            ]}
          />
        ) : null}
      </div>
    </div>
  );
}

/** Líneas por hora UTC, con una línea de objetivo opcional. */
export function LineasHora({
  series,
  objetivo,
  referencia,
  etiqueta,
  formatoY,
  formato = formatoY,
  alto = 240,
}: {
  /** `nombre`, `marca` (fondo) y `punto` (relleno), del mismo color que `clase`, dan su fila en la etiqueta y su punto. */
  series: Array<{ valores: Array<number | null>; clase: string; nombre?: string; marca?: string; punto?: string }>;
  objetivo?: { valor: number; texto: string };
  referencia: Date;
  etiqueta: string;
  formatoY: (v: number) => string;
  /** Cifra exacta al pasar el ratón (por defecto, la del eje). */
  formato?: (v: number) => string;
  alto?: number;
}) {
  const valores = series.flatMap((s) => s.valores.filter((v): v is number => v !== null));
  const { tope: max, marcas } = escalaRedonda(Math.max(objetivo?.valor ?? 0, ...valores));
  const { ref, ancho, x, p } = useLienzoHora((hr) => series.some((s) => s.valores[hr] != null));
  const h = alto - ABAJO - ARRIBA;
  const y = (v: number) => ARRIBA + h * (1 - v / max);
  const hm = p.i;
  const filas: FilaEtiqueta[] =
    hm === null
      ? []
      : series.map((s, j) => ({
          etiqueta: s.nombre ?? String(j + 1),
          valor: s.valores[hm] == null ? SIN_VALOR : formato(s.valores[hm]!),
          marca: s.marca,
        }));
  if (hm !== null && objetivo) filas.push({ etiqueta: objetivo.texto, valor: formato(objetivo.valor), marca: 'border-oficina-trabajando', forma: 'discontinua', tenue: true });
  return (
    <div ref={ref} className="w-full">
      <div {...p.props} className={cn('relative', FOCO)} aria-label={etiqueta}>
        <svg viewBox={`0 0 ${ancho} ${alto}`} className="block h-auto w-full" role="img" aria-label={etiqueta}>
          <Ejes ancho={ancho} alto={alto} marcas={marcas} formatoY={formatoY} referencia={referencia} />
          {hm !== null ? <line x1={x(hm + 0.5)} x2={x(hm + 0.5)} y1={ARRIBA} y2={alto - ABAJO} className="stroke-oficina-texto3" /> : null}
          {objetivo ? (
            <g>
              <line x1={IZQ} x2={ancho - DER} y1={y(objetivo.valor)} y2={y(objetivo.valor)} strokeDasharray="6 5" strokeWidth={1.5} className="stroke-oficina-trabajando" />
              <text x={ancho - DER - 4} y={y(objetivo.valor) - 6} textAnchor="end" className="fill-oficina-trabajando text-o-nota">
                {objetivo.texto}
              </text>
            </g>
          ) : null}
          {series.map((s, j) => (
            <polyline
              key={j}
              points={s.valores
                .map((v, i) => (v === null ? null : `${x(i + 0.5).toFixed(1)},${y(v).toFixed(1)}`))
                .filter(Boolean)
                .join(' ')}
              fill="none"
              strokeWidth={2.5}
              strokeLinejoin="round"
              className={s.clase}
            />
          ))}
          {hm !== null
            ? series.map((s, j) =>
                s.valores[hm] == null ? null : (
                  <circle key={j} cx={x(hm + 0.5)} cy={y(s.valores[hm]!)} r={4.5} strokeWidth={2} className={cn(s.punto ?? 'fill-oficina-texto', 'stroke-oficina-elevada')} />
                ),
              )
            : null}
        </svg>
        {hm !== null && p.activa ? <Etiqueta x={x(hm + 0.5)} ancho={ancho} arriba={ARRIBA} titulo={franjaHora(hm)} filas={filas} /> : null}
      </div>
    </div>
  );
}

/** «Ver las 25» / «Ver menos» bajo una tabla recortada (Pau 10-10: la tabla enseñaba solo una parte sin decirlo). */
export function VerTodas({ total, abiertas, alCambiar }: { total: number; abiertas: boolean; alCambiar: () => void }) {
  return (
    <button
      type="button"
      onClick={alCambiar}
      className="mt-1 inline-flex min-h-[36px] items-center rounded-o-s px-3 text-o-sub font-medium text-oficina-texto2 outline-none hover:bg-oficina-hover hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-oficina-acento"
    >
      {abiertas ? t('salaControl.verMenos') : t('salaControl.verTodas', { n: numero(total) })}
    </button>
  );
}

/** Una lista que enseña las primeras y se despliega con «y N más» (Obra, Taller). */
export function ListaPlegable<T>({
  items,
  primeras = 3,
  pinta,
  clave,
  yMas,
  verMenos,
}: {
  items: T[];
  primeras?: number;
  pinta: (x: T) => ReactNode;
  clave: (x: T) => string;
  yMas: (n: number) => string;
  verMenos: string;
}) {
  const [todo, setTodo] = useState(false);
  const vista = todo ? items : items.slice(0, primeras);
  return (
    <>
      <ul className="flex flex-col gap-1.5">{vista.map((x) => <Fragment key={clave(x)}>{pinta(x)}</Fragment>)}</ul>
      {items.length > primeras ? (
        <button type="button" onClick={() => setTodo(!todo)} className="mt-1.5 min-h-11 text-left text-o-nota text-oficina-enlace hover:underline sm:min-h-0">
          {todo ? verMenos : yMas(items.length - primeras)}
        </button>
      ) : null}
    </>
  );
}
